import React, { Component, ComponentType, ReactNode } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { AppState } from '@om-fe/core/core/types';
import {
    FilteredEventInfos,
    FilteredMatchesWithEvents,
    MatchListStoreModel,
} from '../match-list/utils/match-list-models';
import { I18nContextInterface } from '@om-fe/core/core/i18n/utils';
import { Breakpoints, BreakpointsState } from '@om-fe/core/core/store/breakpoints';
import withTranslation from '@om-fe/core/core/i18n/with-translation';
import { BemMethod, createBem, isLiveStatus, PlatformTypes, preventEvent } from '@om-fe/utils';
import { RouteNames } from '@om-fe/core/core/routing/utils';
import { makeGetServePossessionParticipantId } from '@om-fe/core/components/match-list/containers/live-match-list/live-match-list.selector';
import { IconSize } from '@om-fe/core/utils/config';
import EventAnchor from '@om-fe/core/components/event-anchor/event-anchor';
import MatchInfoScore from './match-info-score';
import getSlice from '@om-fe/core/utils/get-slice';
import { ParticipantIcon } from '@om-fe/widgets/dist/components/icon/participant-icon';
import { DisciplineIcon } from '@om-fe/widgets/dist/components/icon/discipline-icon';
import { getTimeUntilMatchStart } from '@om-fe/core/components/countdown/countdown.utils';
import { EventItemCountdown } from '@om-fe/core/components/countdown/event-item-countdown';
import MatchTime from '../match-time/match-time';
import { EventStatus, ModelTypes } from '@om-fe/models';
import { MatchListItemLayout } from '../match-list/utils/match-list.constants';
import getConfig from '@om-fe/core/utils/config';
import FavoriteTeamsButton from '@om-fe/core/components/favorite-teams/favorite-teams-button';
import { setActiveMarketGroup } from '../match-list/containers/tournament-match-list/tournament-match-list.actions';

interface MatchInfoDefaultProps {
    readonly routeName: RouteNames;
}

interface MatchInfoDetailsState {
    readonly isCountdownFinished: boolean;
}

interface MatchInfoPublicProps extends Partial<MatchInfoDefaultProps> {
    readonly matchId: string;
    readonly sliceKey: string;
    readonly isEventSlider?: boolean;
    readonly bemItem: BemMethod;
    readonly hasStreamingProviderId?: boolean;
    readonly modelType: ModelTypes;
    readonly isVirtual?: boolean;
    readonly matchListInstance?: string;
    readonly itemLayout?: MatchListItemLayout;
}

interface MatchInfoStoreProps {
    readonly isLive?: boolean;
    readonly isSmall?: boolean;
    readonly match?: FilteredMatchesWithEvents;
    readonly servePossessionParticipantId?: string;
    readonly matchTime?: number;
    readonly timeLeft?: number;
    readonly currentEventPart?: string;
    readonly currentEventPartParam?: string;
    readonly numberOfMarkets?: number;
    readonly deviceType?: PlatformTypes;
    readonly redCardsInfo?: FilteredEventInfos; 
}

interface MatchInfoDispatchProps {
    readonly setMarketGroup?: (instanceId: string, matchId: string) => void;
}

interface RedCardsObj {
    readonly home: string | number;
    readonly away: string | number;
}

type MatchInfoProps = MatchInfoPublicProps
    & MatchInfoDefaultProps
    & MatchInfoStoreProps
    & I18nContextInterface
    & MatchInfoDispatchProps;

const bem = createBem({
    name: 'Details',
});

class MatchInfoComponent extends Component<MatchInfoProps, MatchInfoDetailsState> {
    static defaultProps: MatchInfoDefaultProps = {
        routeName: RouteNames.liveEvent,
    };

    state: MatchInfoDetailsState = {
        isCountdownFinished: false,
    };

    getParticipantIcon(participantLogoUrl: string): ReactNode {
        const { match } = this.props;

        if (!match) {
            return;
        }

        return (
            <span className={bem('ParticipantIconWrapper')}>
                <ParticipantIcon
                    logoUrl={participantLogoUrl}
                    disciplineId={match.sportId}
                    size={IconSize.Medium2}
                />
            </span>
        );
    }

    handleCountdownStatus = (): void => {
        this.setState({
            isCountdownFinished: true,
        });
    };

    getMatchTimeNode(): ReactNode {
        const { match, matchTime, timeLeft, currentEventPart, currentEventPartParam, isSmall, gettext, hasStreamingProviderId, isVirtual } = this.props;
        const timeLeftText = isSmall ? gettext('Left') : gettext('Time Left');

        if (!match) {
            return null;
        }

        const { statusId, startTime } = match;
        const timeUntilStartInSeconds: number = getTimeUntilMatchStart(startTime);

        if (isLiveStatus(statusId) && timeUntilStartInSeconds > 0 && !isVirtual) {
            return (
                <EventItemCountdown
                    secondsLeft={timeUntilStartInSeconds}
                    onTimerEnd={this.handleCountdownStatus}
                    bem={bem}
                />
            );
        } else if ((isLiveStatus(statusId) || (matchTime || timeLeft)) && !isVirtual) {
            return (
                <MatchTime
                    isMatchLive={true}
                    sportId={match.sportId}
                    timeLeft={timeLeft}
                    matchTime={matchTime}
                    eventPartName={currentEventPart}
                    currentEventPartParam={currentEventPartParam}
                    timeLeftText={timeLeftText}
                    minuteSign="'"
                    className={bem('Info')}
                    hasStreamingProviderId={hasStreamingProviderId}
                />
            );
        }

        return (
            <MatchTime
                isMatchLive={false}
                sportId={match.sportId}
                startTime={startTime}
                className={bem('Info')}
                hasStreamingProviderId={hasStreamingProviderId && !isVirtual}
                showFullDate={isVirtual}
            />
        );
    }

    getNumberOfMarketsNode = (): ReactNode => {
        const { numberOfMarkets } = this.props;

        if (!numberOfMarkets) {
            return null;
        }

        return (
            <span className={bem('IndicatorPart')}>
                <span>,</span>
                <span>+</span>
                <span>{numberOfMarkets}</span>
            </span>
        );
    };

    getDescriptorsNode = (): ReactNode => {
        const {
            match,
            bemItem,
            deviceType,
        } = this.props;

        const {
            desktop: showOnDesktop = false,
            tablet: showOnTablet = false,
            mobile: showOnMobile = false,
        } = getConfig().numberOfMarketsUnderTeamsName ?? {};

        const isDesktop: boolean = deviceType === 'desktop';
        const isTablet: boolean = deviceType === 'tablet';
        const isMobile: boolean = deviceType === 'mobile';

        const showNumberOfMarkets: boolean = (showOnDesktop && isDesktop) || (showOnTablet && isTablet) || (showOnMobile && isMobile);

        if (!match) {
            return null;
        }

        return (
            <span className={bemItem('Descriptors')}>
                <span className={bemItem('InfoGroup')}>
                    {this.getMatchTimeNode()}
                </span>
                {showNumberOfMarkets && this.getNumberOfMarketsNode()}
            </span>
        );
    };

    pickCorrectParticipant = (): RedCardsObj => {
        const { homeParticipantId, redCardsInfo } = this.props

        if (!redCardsInfo) {
            return ({
                home: 0,
                away: 0,
            })
        }

        let redCardsObj: RedCardsObj = {
            home: redCardsInfo.paramFloat2,
            away: redCardsInfo.paramFloat1,
        };

        if (homeParticipantId === redCardsInfo.paramParticipantId1) {
            redCardsObj = {
                home: redCardsInfo.paramFloat1,
                away: redCardsInfo.paramFloat2,
            };
        }

        return redCardsObj;
    }

    getMatchInfoContent = (): ReactNode => {
        const {
            match,
            isSmall,
            matchId,
            sliceKey,
            servePossessionParticipantId,
            modelType,
            isVirtual,
            itemLayout,
        } = this.props;

        if (!match) {
            return null;
        }

        const {
            homeParticipantName, awayParticipantName, homeShortParticipantName, awayShortParticipantName,
            homeParticipantId, awayParticipantId,
            homeParticipantLogoUrl, awayParticipantLogoUrl,
            sportId,
            shortParentName,
        } = match;

        const isOutright: boolean = modelType === ModelTypes.TOURNAMENT ? true : false;
        const homeName: string = isSmall ? homeShortParticipantName : homeParticipantName;
        const awayName: string = isSmall ? awayShortParticipantName : awayParticipantName;
        const servePossessionHome = isOutright ? false : (homeParticipantId === servePossessionParticipantId);
        const servePossessionAway = isOutright ? false : (awayParticipantId === servePossessionParticipantId);

        return (
            <>
                <span className={bem('Group', { HasParticipantIcon: !!(homeParticipantLogoUrl || awayParticipantLogoUrl) })}>
                    <span className={bem('Participants')}>
                        <span className={bem('Participant', { Home: true, ServePossession: servePossessionHome })}>
                            {homeParticipantLogoUrl && this.getParticipantIcon(homeParticipantLogoUrl)}
                            {!isOutright && !isVirtual &&
                                <div className={bem('FavoriteButton')} onClick={preventEvent}>
                                    <FavoriteTeamsButton
                                        participantId={homeParticipantId}
                                        participantName={homeName}
                                        competitionName={shortParentName}
                                    />
                                </div>
                            }
                            <span className={bem('ParticipantName')}>
                                {homeName}
                            </span>
                        </span>
                        <span className={bem('Participant', { Away: true, ServePossession: servePossessionAway })}>
                            {awayParticipantLogoUrl && this.getParticipantIcon(awayParticipantLogoUrl)}
                            {!isOutright && !isVirtual &&
                                <div className={bem('FavoriteButton')} onClick={preventEvent}>
                                    <FavoriteTeamsButton
                                        participantId={awayParticipantId}
                                        participantName={awayName}
                                        competitionName={shortParentName}
                                    />
                                </div>
                            }
                            <span className={bem('ParticipantName')}>
                                {awayName}
                            </span>
                        </span>
                    </span>
                    <MatchInfoScore
                        matchId={matchId}
                        sliceKey={sliceKey}
                    />
                </span>
                {this.getDescriptorsNode()}
                {itemLayout === MatchListItemLayout.Special && (
                    <DisciplineIcon disciplineId={sportId} />
                )}
            </>
        );
    };

    render() {
        const {
            match,
            matchId,
            isLive,
            modelType,
            isVirtual,
            setMarketGroup,
            matchListInstance,
        } = this.props;

        if (!match) {
            return null;
        }

        const isOutright: boolean = modelType === ModelTypes.TOURNAMENT ? true : false;
        const isHyphenatedFullUrl: boolean = !!getConfig().enableHyphenatedUrl?.fullName;
        const locationName = isHyphenatedFullUrl ? match?.venueName : match?.shortVenueName;
        const disciplineName = isHyphenatedFullUrl ? match?.sportName : match?.shortSportName;
        const tournamentName = isHyphenatedFullUrl ? match?.parentName : match?.shortParentName;

        const {
            sportId: disciplineId, name: eventName,
        } = match;

        return (
            isVirtual && matchListInstance ? (
                <div 
                    className={bem()} 
                    onClick={() => setMarketGroup?.(matchListInstance, matchId)}
                >
                    {this.getMatchInfoContent()}
                </div>
            ) : (
                <EventAnchor
                    className={bem()}
                    isLive={!!isLive}
                    disciplineId={disciplineId}
                    disciplineName={disciplineName}
                    locationName={locationName}
                    tournamentName={tournamentName}
                    eventId={matchId}
                    eventName={eventName}
                    isOutright={isOutright}
                >
                    {this.getMatchInfoContent()}
                </EventAnchor>
            )
        );
    }
}

const MatchInfoV2: ComponentType<MatchInfoPublicProps> = compose<ComponentType<MatchInfoPublicProps>>(
    withTranslation<MatchInfoPublicProps>(),
    connect<MatchInfoStoreProps>(
        () => {
            const getServePossessionParticipantId = makeGetServePossessionParticipantId();

            return (state: AppState, props: MatchInfoPublicProps): MatchInfoStoreProps => {
                const { matchId } = props;
                const slice: MatchListStoreModel | undefined = getSlice<MatchListStoreModel>(state, props.sliceKey);
                const breakpoints: BreakpointsState | null = state.breakpoints;

                if (!slice || !breakpoints) {
                    return {};
                }

                const match: FilteredMatchesWithEvents | undefined = slice.filteredMatchesWithEvents[matchId];
                const isSmall: boolean | undefined = breakpoints.small;
                const isLive: boolean = !!match && isLiveStatus(match.statusId as EventStatus);
                const eventInfo: Record<string, FilteredEventInfos> = slice.filteredEventInfos;
                const matchTimeId: string | undefined = match?.matchTimeId;
                const currentEventPartId: string | undefined = match?.currentEventPartId;
                const remainingTimeId: string | undefined = match?.remainingTimeId;
                const redCardsId: string | undefined = match?.redCardsId;

                return {
                    isLive,
                    isSmall,
                    match,
                    matchTime: matchTimeId && slice.filteredEventInfos[matchTimeId] ?
                        slice.filteredEventInfos[matchTimeId].paramFloat1 : undefined,
                    timeLeft: remainingTimeId && slice.filteredEventInfos[remainingTimeId] ?
                        slice.filteredEventInfos[remainingTimeId].paramFloat1 : undefined,
                    currentEventPart: currentEventPartId
                        ? (state.breakpoints[Breakpoints.XL] || state.breakpoints[Breakpoints.Large])
                            ? slice.filteredEventInfos[currentEventPartId].paramEventPartName1
                            : slice.filteredEventInfos[currentEventPartId].shortParamEventPartName1
                        : undefined,
                    currentEventPartParam: currentEventPartId && slice.filteredEventInfos[currentEventPartId] ?
                        slice.filteredEventInfos[currentEventPartId].paramEventPartId1 : undefined,
                    servePossessionParticipantId: getServePossessionParticipantId(eventInfo, matchId),
                    numberOfMarkets: match.numberOfMarkets,
                    deviceType: state.deviceInfo?.platform.type,
                    redCardsInfo: redCardsId && slice.filteredEventInfos[redCardsId]
                        ? slice.filteredEventInfos[redCardsId] : undefined,
                };
            };
        },
        {
            setMarketGroup: setActiveMarketGroup,
        }
    ),
)(MatchInfoComponent);

export default MatchInfoV2;
